import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import ContactSection from '../components/ContactSection';
import { FALLBACK_PROJECTS, SITE_INFO } from '../data';
import { fetchCollection } from '../lib/cms';

export default function ProjectsOverview() {
  const [projects, setProjects] = useState(FALLBACK_PROJECTS);

  useEffect(() => {
    async function loadProjects() {
      const cmsProjects = await fetchCollection('projects');
      if (cmsProjects && cmsProjects.length > 0) {
        setProjects(cmsProjects);
      }
    }
    loadProjects();
  }, []);

  return (
    <>
      <SEOHead 
        title={`Projekter & Referencer - ${SITE_INFO.name}`}
        description="Gå på opdagelse i vores udførte tømrer- og snedkerprojekter i Nordsjælland. Billeder og detaljer fra tagrenoveringer, snedkerkøkkener og træterrasser."
      />

      {/* Hero Header */}
      <section className="bg-stone-900 text-white py-16 sm:py-20 border-b border-stone-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <div className="max-w-3xl space-y-4">
            <h1 className="font-heading font-extrabold text-4xl sm:text-5xl text-white tracking-tight">
              Projekter & Referencer
            </h1>
            <p className="text-stone-300 text-base sm:text-lg leading-relaxed">
              Her kan du se et udvalg af vores seneste byggeprojekter udført for private boligejere og arkitekter.
            </p>
          </div>
        </div>
      </section>

      {/* Editorial Grid */}
      <section className="py-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            {projects.map((proj) => {
              const coverImg = Array.isArray(proj.cover_image) ? proj.cover_image[0] : proj.cover_image;

              return (
                <Link
                  key={proj.url_slug || proj.id}
                  to={`/projekter/${proj.url_slug}`}
                  className="group bg-white overflow-hidden border border-stone-200 shadow-md hover:shadow-xl transition-all duration-300 flex flex-col"
                >
                  <div className="relative h-80 overflow-hidden">
                    <img
                      src={`https://assets.auxodev.dk/cdn-cgi/image/width=1000,quality=85,format=auto/${coverImg.replace('https://assets.auxodev.dk/', '')}`}
                      alt={proj.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-4 left-4 bg-stone-900/90 text-white text-xs font-semibold px-3 py-1.5 backdrop-blur-sm border border-stone-700">
                      {proj.location || 'Nordsjælland'} • {proj.year || '2025'}
                    </div>
                  </div>

                  <div className="p-8 flex-1 flex flex-col justify-between space-y-4">
                    <div>
                      <h2 className="font-heading font-bold text-2xl text-stone-900 group-hover:text-amber-700 transition-colors">
                        {proj.title}
                      </h2>
                      <p className="text-stone-600 text-sm leading-relaxed mt-2 line-clamp-3">
                        {proj.description}
                      </p>
                    </div>

                    <div className="pt-4 border-t border-stone-100 flex items-center justify-between text-xs font-bold uppercase tracking-wider text-amber-700">
                      <span>Se Billeder & Detaljer</span>
                      <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" aria-hidden="true" />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <ContactSection />
    </>
  );
}
